import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Trash2 } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  brand?: string | null;
  category?: string | null;
  description?: string | null;
  price: number;
  currency: string;
  stock_status: string;
  stock_quantity: number;
  image_filename?: string | null;
  url?: string | null;
  tags?: string | null;
  is_active: boolean;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface ProductForm {
  name: string;
  brand: string;
  category: string;
  description: string;
  price: string;
  currency: string;
  stock_status: string;
  stock_quantity: string;
  url: string;
  tags: string;
  is_active: boolean;
}

const emptyForm: ProductForm = {
  name: '',
  brand: '',
  category: '',
  description: '',
  price: '',
  currency: 'gbp',
  stock_status: 'in_stock',
  stock_quantity: '0',
  url: '',
  tags: '',
  is_active: true,
};

const stockStatusColors: Record<string, string> = {
  in_stock: '#16A34A',
  low_stock: '#F59E0B',
  out_of_stock: '#EF4444',
};

const AdminProductInventory: React.FC = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ProductForm | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API.listProducts(), { headers: authHeaders() });
      setProducts(response.data as Product[]);
    } catch (err: any) {
      console.error('Failed to load products:', err);
      setError(err.response?.data?.detail || 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) fetchData();
  }, [fetchData, user]);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))) as string[],
    [products],
  );

  const filteredProducts = useMemo(() => {
    if (!categoryFilter) return products;
    return products.filter((p) => p.category === categoryFilter);
  }, [products, categoryFilter]);

  const openCreateModal = () => {
    setCreating(true);
    setEditForm({ ...emptyForm });
  };

  const openEditModal = (product: Product) => {
    setEditProductId(product.id);
    setEditForm({
      name: product.name,
      brand: product.brand || '',
      category: product.category || '',
      description: product.description || '',
      price: String(product.price),
      currency: product.currency,
      stock_status: product.stock_status,
      stock_quantity: String(product.stock_quantity),
      url: product.url || '',
      tags: product.tags || '',
      is_active: product.is_active,
    });
  };

  const closeModal = () => {
    setCreating(false);
    setEditProductId(null);
    setEditForm(null);
  };

  const submitForm = async () => {
    if (!editForm) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: editForm.name,
        brand: editForm.brand || null,
        category: editForm.category || null,
        description: editForm.description || null,
        price: Number(editForm.price),
        currency: editForm.currency || 'gbp',
        stock_status: editForm.stock_status,
        stock_quantity: Number(editForm.stock_quantity) || 0,
        url: editForm.url || null,
        tags: editForm.tags || null,
        is_active: editForm.is_active,
      };
      if (creating) {
        await axios.post(API.createProduct(), payload, { headers: authHeaders() });
        setMessage('Product created.');
      } else if (editProductId) {
        await axios.put(API.updateProduct(editProductId), payload, { headers: authHeaders() });
        setMessage('Product updated.');
      }
      closeModal();
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProductId) return;
    setDeleting(true);
    setError('');
    try {
      await axios.delete(API.deleteProduct(deleteProductId), { headers: authHeaders() });
      setMessage('Product deleted.');
      setDeleteProductId(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete product.');
    } finally {
      setDeleting(false);
    }
  };

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Product Inventory</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={fetchData}>Refresh</button>
          <button className="btn btn-primary" onClick={openCreateModal}>Add Product</button>
        </div>
      </div>

      <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-20px', marginBottom: '24px' }}>
        This catalog is what the Telegram AI Sourcing Concierge searches when recommending items to customers.
      </p>

      {(error || message) && (
        <div style={{
          marginBottom: '24px', padding: '16px', borderRadius: '8px', fontSize: '14px',
          background: error ? '#FEF2F2' : '#F0FDF4',
          color: error ? '#991B1B' : '#166534',
          border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
        }}>
          {error || message}
        </div>
      )}

      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[{ label: 'All', value: '' }, ...categories.map((c) => ({ label: c, value: c }))].map((f) => (
            <button
              key={f.label}
              onClick={() => setCategoryFilter(f.value)}
              className="btn"
              style={{
                padding: '8px 20px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: categoryFilter === f.value ? '#1C1C1C' : 'transparent',
                color: categoryFilter === f.value ? '#fff' : '#1C1C1C',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--sidebar-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--sidebar-border)' }}>
                <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Product</th>
                <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Category</th>
                <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px' }}>Stock</th>
                <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px', textAlign: 'right' }}>Price</th>
                <th style={{ padding: '14px 20px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: '11px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>Loading...</td></tr>
              ) : filteredProducts.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>No products yet — add one to get started.</td></tr>
              ) : filteredProducts.map((product) => (
                <tr key={product.id} style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 600, color: '#0F172A' }}>
                      {product.url ? (
                        <a href={product.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                          {product.name}
                        </a>
                      ) : (
                        product.name
                      )}
                    </div>
                    {product.brand && <div style={{ fontSize: '12px', color: '#64748B' }}>{product.brand}</div>}
                    {!product.is_active && (
                      <span className="status-pill" style={{ background: '#F1F5F9', color: '#64748B', marginTop: '4px', display: 'inline-block' }}>HIDDEN</span>
                    )}
                    {product.last_synced_at && (
                      <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px' }}>
                        Synced {new Date(product.last_synced_at).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px', color: '#334155' }}>{product.category || '—'}</td>
                  <td style={{ padding: '16px 20px' }}>
                    <span className="status-pill" style={{
                      background: `${stockStatusColors[product.stock_status] || '#64748B'}15`,
                      color: stockStatusColors[product.stock_status] || '#64748B',
                      display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content',
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }}></div>
                      {product.stock_status.replace('_', ' ').toUpperCase()}
                    </span>
                    <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>Qty: {product.stock_quantity}</div>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: 700 }}>
                    {product.currency.toUpperCase() === 'GBP' ? '£' : product.currency.toUpperCase() + ' '}{product.price.toFixed(2)}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                        onClick={() => openEditModal(product)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto', color: '#DC2626', borderColor: '#FCA5A5' }}
                        onClick={() => setDeleteProductId(product.id)}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '520px', margin: '20px', maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 className="card-title">{creating ? 'Add Product' : 'Edit Product'}</h2>

            <div className="form-group">
              <label className="form-label" htmlFor="product-name-input">Name</label>
              <input id="product-name-input" className="form-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="product-brand-input">Brand</label>
                <input id="product-brand-input" className="form-input" value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="product-category-input">Category</label>
                <input id="product-category-input" className="form-input" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} placeholder="shoes, outerwear..." />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="product-description-input">Description</label>
              <textarea
                id="product-description-input"
                className="form-input"
                style={{ minHeight: '80px' }}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="product-price-input">Price</label>
                <input id="product-price-input" type="number" step="0.01" className="form-input" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="product-currency-input">Currency</label>
                <input id="product-currency-input" className="form-input" value={editForm.currency} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="product-quantity-input">Stock Qty</label>
                <input id="product-quantity-input" type="number" min="0" className="form-input" value={editForm.stock_quantity} onChange={(e) => setEditForm({ ...editForm, stock_quantity: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="product-stock-status-select">Stock Status</label>
                <select id="product-stock-status-select" className="form-input" value={editForm.stock_status} onChange={(e) => setEditForm({ ...editForm, stock_status: e.target.value })}>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="product-active-select">Visible to Concierge</label>
                <select
                  id="product-active-select"
                  className="form-input"
                  value={editForm.is_active ? 'true' : 'false'}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'true' })}
                >
                  <option value="true">Active</option>
                  <option value="false">Hidden</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="product-url-input">Product URL</label>
              <input id="product-url-input" className="form-input" value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} placeholder="https://brand.com/product" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="product-tags-input">Tags (comma-separated)</label>
              <input id="product-tags-input" className="form-input" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} placeholder="cashmere,outerwear,ss26" />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={submitForm} disabled={saving || !editForm.name || !editForm.price}>
                {saving ? 'Saving...' : creating ? 'Create Product' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteProductId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '400px', margin: '20px', textAlign: 'center' }}>
            <h2 className="card-title" style={{ color: '#DC2626' }}>Delete Product</h2>
            <p style={{ margin: '16px 0', color: '#64748B', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to permanently delete this product?<br />
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setDeleteProductId(null)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#DC2626' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductInventory;
